import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { AlertTriangle, Scissors, Timer, Users, Trash2, X } from 'lucide-react'
import type { Task } from '@/lib/types'

interface RescheduleNudgeModalProps {
  isOpen: boolean
  onClose: () => void
  task: Task | null
  onSplit: () => void
  onFiveMin: () => void
  onDelegate: () => void
  onDelete: () => void
}

const actions = [
  {
    id: 'split' as const,
    icon: Scissors,
    title: '더 작게 쪼개기',
    description: '작업을 작은 단위로 나눠보세요',
    color: 'text-blue-500',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
  },
  {
    id: 'fivemin' as const,
    icon: Timer,
    title: '5분만 시작하기',
    description: '포커스 모드로 5분만 집중해보세요',
    color: 'text-green-500',
    bg: 'bg-green-50 dark:bg-green-900/20',
  },
  {
    id: 'delegate' as const,
    icon: Users,
    title: '위임하기',
    description: '다른 사람에게 맡길 수 있을까요?',
    color: 'text-purple-500',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
  },
  {
    id: 'delete' as const,
    icon: Trash2,
    title: '삭제하기',
    description: '정말 필요한 작업이 아닐 수 있어요',
    color: 'text-red-500',
    bg: 'bg-red-50 dark:bg-red-900/20',
  },
]

export function RescheduleNudgeModal({
  isOpen,
  onClose,
  task,
  onSplit,
  onFiveMin,
  onDelegate,
  onDelete,
}: RescheduleNudgeModalProps) {
  const count = task?.rescheduleCount || 0

  const handleAction = (id: string) => {
    switch (id) {
      case 'split': onSplit(); break
      case 'fivemin': onFiveMin(); break
      case 'delegate': onDelegate(); break
      case 'delete': onDelete(); break
    }
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="dialog-backdrop" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="dialog-panel w-full max-w-sm rounded-2xl p-0 overflow-hidden">
          {/* Header */}
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <DialogTitle className="text-base font-semibold text-heading">
                    미루기 감지
                  </DialogTitle>
                  <p className="text-xs text-caption mt-0.5">
                    이 작업이 <span className="font-semibold text-amber-500">{count}번</span> 미뤄졌습니다
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-disabled hover:text-[var(--text-secondary)] hover-surface transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {task && (
              <p className="mt-3 px-3 py-2 text-sm text-body bg-surface-secondary rounded-lg truncate">
                "{task.title}"
              </p>
            )}

            <p className="mt-3 text-sm text-caption">
              어떻게 할까요?
            </p>
          </div>

          {/* Action cards */}
          <div className="px-5 pb-5 space-y-2">
            {actions.map((action) => {
              const Icon = action.icon
              return (
                <button
                  key={action.id}
                  onClick={() => handleAction(action.id)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-strong hover:border-[var(--hover-border)] hover:shadow-sm transition-all text-left"
                >
                  <div className={`w-9 h-9 rounded-lg ${action.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4.5 h-4.5 ${action.color}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-heading">{action.title}</p>
                    <p className="text-xs text-caption">{action.description}</p>
                  </div>
                </button>
              )
            })}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}
